import React, { useState, useEffect } from 'react';

interface PlatformStats {
  views: number | null;
  reactions: number | null;
  comments: number | null;
  url: string;
  lastSyncedAt: string;
  error?: string;
}

interface PlatformsData {
  schemaVersion: 1;
  lastSyncedAt: string;
  devto?: PlatformStats;
  hashnode?: PlatformStats;
}

interface ProbeResult {
  engine: string;
  query: string;
  citation: 'hit' | 'miss' | 'error';
  position?: number;
  citedAt: string;
  error?: string;
}

interface GeoData {
  schemaVersion: 1;
  lastSyncedAt: string;
  probes: ProbeResult[];
  botVisits: Record<string, number>;
  shareOfVoice: Record<string, number>;
  queries: string[];
  error?: string;
}

interface SeoData {
  schemaVersion: 1;
  lastSyncedAt: string;
  window?: { start: string; end: string };
  gsc?: {
    impressions: number;
    clicks: number;
    ctr: number;
    avgPosition: number;
    topQueries: Array<{ query: string; clicks: number; position: number }>;
  };
  indexed?: boolean;
  error?: string;
}

interface PerformanceTabProps {
  brand: string;
  slug: string;
}

function StatCard({ label, value, url }: { label: string; value: number | null | undefined; url?: string }) {
  const display = value === null || value === undefined ? '—' : value.toLocaleString();
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'var(--text-tertiary, #64748b)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary, #f1f5f9)', lineHeight: 1 }}>{display}</div>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--accent)', marginTop: 4, display: 'inline-block' }}>View →</a>
      )}
    </div>
  );
}

function PlatformSection({ title, stats }: { title: string; stats?: PlatformStats }) {
  if (!stats) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary, #94a3b8)', marginBottom: 10 }}>{title}</div>
      {stats.error ? (
        <div style={{ color: 'var(--danger)', fontSize: 12, padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 0 }}>Error: {stats.error}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <StatCard label="Views" value={stats.views} url={stats.url} />
          <StatCard label="Reactions" value={stats.reactions} />
          <StatCard label="Comments" value={stats.comments} />
        </div>
      )}
    </div>
  );
}

function GeoEngineRow({ engine, probes, sov }: { engine: string; probes: ProbeResult[]; sov: number }) {
  const hits = probes.filter(p => p.citation === 'hit').length;
  const total = probes.length;
  const pct = Math.round(sov * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border, #334155)' }}>
      <div style={{ width: 130, fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #f1f5f9)', flexShrink: 0 }}>{engine}</div>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
        {probes.map((p, i) => (
          <span key={i} title={p.query} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 22, height: 22, borderRadius: '50%', fontSize: 12,
            // hit=green, miss=orange(danger). 'error' (probe couldn't run) uses a
            // neutral muted fill so it stays distinct from 'miss' — the brand makes
            // --warning and --danger both orange, which would otherwise collapse the two.
            background: p.citation === 'hit' ? 'var(--success-light)' : p.citation === 'error' ? 'var(--bg-hover)' : 'var(--danger-light)',
            color: p.citation === 'hit' ? 'var(--success)' : p.citation === 'error' ? 'var(--text-tertiary)' : 'var(--danger)',
            cursor: 'default',
          }}>
            {p.citation === 'hit' ? '✓' : p.citation === 'error' ? '!' : '✗'}
          </span>
        ))}
      </div>
      <div style={{ marginLeft: 'auto', fontSize: 13, whiteSpace: 'nowrap' as const }}>
        <span style={{ fontWeight: 700, color: hits > 0 ? 'var(--success)' : 'var(--text-tertiary, #64748b)' }}>{hits}/{total}</span>
        <span style={{ color: 'var(--text-tertiary, #64748b)', marginLeft: 6 }}>({pct}%)</span>
      </div>
    </div>
  );
}

function SyncButton({ onClick, loading, label, loadingLabel }: { onClick: () => void; loading: boolean; label: string; loadingLabel: string }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        fontSize: 12, padding: '6px 12px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border, #334155)',
        borderRadius: 0, cursor: loading ? 'not-allowed' : 'pointer',
        color: 'var(--text-secondary, #94a3b8)',
        opacity: loading ? 0.7 : 1,
      }}
    >
      {loading ? loadingLabel : label}
    </button>
  );
}

export default function PerformanceTab({ brand, slug }: PerformanceTabProps) {
  const [platforms, setPlatforms] = useState<PlatformsData | null>(null);
  const [geo, setGeo] = useState<GeoData | null>(null);
  const [seo, setSeo] = useState<SeoData | null>(null);
  const [platformsLoading, setPlatformsLoading] = useState(true);
  const [geoLoading, setGeoLoading] = useState(true);
  const [syncingPlatforms, setSyncingPlatforms] = useState(false);
  const [syncingGeo, setSyncingGeo] = useState(false);
  const [syncingSeo, setSyncingSeo] = useState(false);
  const [notPublished, setNotPublished] = useState(false);

  useEffect(() => {
    setPlatforms(null);
    setGeo(null);
    setSeo(null);
    setNotPublished(false);
    setPlatformsLoading(true);
    setGeoLoading(true);

    fetch(`/api/${brand}/pieces/${slug}/observability`)
      .then(r => {
        if (r.status === 404) {
          // Not yet synced — check if piece has publications to decide messaging
          fetch(`/api/${brand}/pieces/${slug}`)
            .then(r2 => r2.json())
            .then(detail => {
              const pubs = detail?.manifest?.publications ?? {};
              setNotPublished(Object.keys(pubs).length === 0);
            })
            .catch(() => {});
          setPlatforms(null);
          setPlatformsLoading(false);
          return null;
        }
        return r.json();
      })
      .then(d => {
        if (d !== null) { setPlatforms(d); setPlatformsLoading(false); }
      })
      .catch(() => setPlatformsLoading(false));

    fetch(`/api/${brand}/pieces/${slug}/observability/geo`)
      .then(r => r.status === 404 ? null : r.json())
      .then(d => { setGeo(d); setGeoLoading(false); })
      .catch(() => setGeoLoading(false));

    fetch(`/api/${brand}/pieces/${slug}/observability/seo`)
      .then(r => r.status === 404 ? null : r.json())
      .then(d => { setSeo(d); })
      .catch(() => {});
  }, [brand, slug]);

  function syncPlatforms() {
    setSyncingPlatforms(true);
    fetch(`/api/${brand}/pieces/${slug}/observability/sync`, { method: 'POST' })
      .then(r => r.json())
      .then(d => { setPlatforms(d); setSyncingPlatforms(false); setNotPublished(false); })
      .catch(() => setSyncingPlatforms(false));
  }

  function syncGeo() {
    setSyncingGeo(true);
    fetch(`/api/${brand}/pieces/${slug}/observability/geo/sync`, { method: 'POST' })
      .then(r => r.json())
      .then(d => { setGeo(d); setSyncingGeo(false); })
      .catch(() => setSyncingGeo(false));
  }

  function syncSeoApi() {
    setSyncingSeo(true);
    // #228: surface the failure. The server returns {error} (e.g. 500 when
    // GOOGLE_SERVICE_ACCOUNT_JSON is unset); set it on state so the SEO panel
    // can render an inline error instead of the button silently going dead.
    fetch(`/api/${brand}/pieces/${slug}/observability/seo/sync`, { method: 'POST' })
      .then(r => r.json())
      .then(d => {
        // On success replace with the fresh payload; on error keep any already-
        // loaded GSC data and attach the error so a failed re-sync doesn't wipe
        // good metrics off the panel.
        setSeo(prev => (d && !d.error ? d : prev ? { ...prev, error: d?.error ?? 'Sync failed' } : d));
        setSyncingSeo(false);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Sync request failed';
        setSeo(prev => prev ? { ...prev, error: msg } : { schemaVersion: 1, lastSyncedAt: new Date().toISOString(), error: msg });
        setSyncingSeo(false);
      });
  }

  const hasDevto = !!platforms?.devto;
  const hasHashnode = !!platforms?.hashnode;
  const hasAnyPlatform = hasDevto || hasHashnode;

  // Group GEO probes by engine
  const engineGroups: Record<string, ProbeResult[]> = {};
  if (geo?.probes) {
    for (const p of geo.probes) {
      if (!engineGroups[p.engine]) engineGroups[p.engine] = [];
      engineGroups[p.engine].push(p);
    }
  }

  const sectionHeader = (title: React.ReactNode, btn: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #f1f5f9)' }}>{title}</div>
      {btn}
    </div>
  );

  return (
    <div style={{ padding: '24px 0', maxWidth: 680 }}>

      {/* Platform Stats */}
      {sectionHeader(
        'Platform Stats',
        <SyncButton onClick={syncPlatforms} loading={syncingPlatforms} label="↻ Sync now" loadingLabel="Syncing..." />,
      )}

      {platformsLoading ? (
        <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13, paddingBottom: 24 }}>Loading platform stats…</div>
      ) : notPublished ? (
        <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13, paddingBottom: 24, fontStyle: 'italic' }}>
          Not published to any platforms yet.
        </div>
      ) : !hasAnyPlatform ? (
        <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13, paddingBottom: 24, fontStyle: 'italic' }}>
          No observability data yet. Click "Sync now" to fetch platform stats.
        </div>
      ) : (
        <div style={{ marginBottom: 8 }}>
          <PlatformSection title="Dev.to" stats={platforms?.devto} />
          <PlatformSection title="Hashnode" stats={platforms?.hashnode} />
          {platforms?.lastSyncedAt && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #64748b)', marginBottom: 28, marginTop: -4 }}>
              Last synced: {new Date(platforms.lastSyncedAt).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* SEO — Google Search Console */}
      <>
        <div style={{ borderTop: '1px solid var(--border, #334155)', marginBottom: 24 }} />
        {sectionHeader(
          <span>
            {'SEO — Google Search Console'}
            {seo?.window && (
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary, #64748b)', marginLeft: 10 }}>
                {seo.window.start} {'→'} {seo.window.end}
              </span>
            )}
          </span>,
          <SyncButton onClick={syncSeoApi} loading={syncingSeo} label="↻ Sync GSC" loadingLabel="Fetching..." />,
        )}
        {seo?.error && (
          <div style={{ color: 'var(--danger)', fontSize: 12, padding: '8px 12px', background: 'var(--danger-light)', borderRadius: 0, marginBottom: 16 }}>
            GSC sync failed: {seo.error}
          </div>
        )}
        {!seo ? (
          <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13, paddingBottom: 24, fontStyle: 'italic' }}>
            No SEO data yet. Click "Sync GSC" to pull live metrics from Google Search Console.
          </div>
        ) : seo.gsc ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              <StatCard label="Clicks" value={seo.gsc.clicks} />
              <StatCard label="Impressions" value={seo.gsc.impressions} />
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, padding: '14px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'var(--text-tertiary, #64748b)', marginBottom: 6 }}>CTR</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary, #f1f5f9)', lineHeight: 1 }}>
                  {(seo.gsc.ctr * 100).toFixed(2)}%
                </div>
              </div>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, padding: '14px 18px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'var(--text-tertiary, #64748b)', marginBottom: 6 }}>Avg Position</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: seo.gsc.avgPosition <= 10 ? 'var(--success)' : seo.gsc.avgPosition <= 20 ? 'var(--warning)' : 'var(--danger)', lineHeight: 1 }}>
                  {seo.gsc.avgPosition.toFixed(1)}
                </div>
              </div>
            </div>
            {seo.gsc.topQueries.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary, #64748b)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Top Queries</div>
                <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, overflow: 'hidden' }}>
                  {seo.gsc.topQueries.slice(0, 8).map((q, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', borderBottom: '1px solid var(--border, #334155)', fontSize: 12 }}>
                      <span style={{ color: 'var(--text-tertiary, #64748b)', width: 18, flexShrink: 0 }}>{i + 1}.</span>
                      <span style={{ color: 'var(--text-secondary, #94a3b8)', flex: 1 }}>{q.query}</span>
                      <span style={{ color: 'var(--text-tertiary, #64748b)', marginLeft: 'auto', whiteSpace: 'nowrap' as const }}>
                        {q.clicks} clicks · pos {q.position.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {seo.indexed === false && (
              <div style={{ fontSize: 12, color: 'var(--danger)', padding: '6px 10px', background: 'var(--danger-light)', borderRadius: 0, marginBottom: 8 }}>
                Not indexed in Google Search
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #64748b)', marginBottom: 24 }}>
              Last synced: {new Date(seo.lastSyncedAt).toLocaleString()}
            </div>
          </>
        ) : null}
      </>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border, #334155)', marginBottom: 24 }} />

      {/* GEO / AI Citation */}
      {sectionHeader(
        'GEO — AI Citation Tracking',
        <SyncButton onClick={syncGeo} loading={syncingGeo} label="↻ Run probe" loadingLabel="Probing AI engines..." />,
      )}

      {syncingGeo && (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary, #64748b)', marginBottom: 12, fontStyle: 'italic' }}>
          Querying ChatGPT Search, Perplexity, and Gemini... this may take 30-60s.
        </div>
      )}

      {geoLoading ? (
        <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13 }}>Loading AI citations…</div>
      ) : !geo ? (
        <div style={{ color: 'var(--text-tertiary, #64748b)', fontSize: 13, fontStyle: 'italic' }}>
          No GEO data yet. Click "Run probe" to check AI engine citations (requires OPENROUTER_API_KEY).
        </div>
      ) : (
        <>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ padding: '9px 14px', borderBottom: '1px solid var(--border, #334155)', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary, #64748b)', display: 'flex', gap: 12, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
              <span style={{ width: 130, flexShrink: 0 }}>Engine</span>
              <span>Queries (hover for text)</span>
              <span style={{ marginLeft: 'auto' }}>Hit rate</span>
            </div>
            {Object.entries(engineGroups).map(([engine, probes]) => (
              <GeoEngineRow key={engine} engine={engine} probes={probes} sov={geo.shareOfVoice?.[engine] ?? 0} />
            ))}
            {Object.keys(engineGroups).length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--text-tertiary, #64748b)', fontStyle: 'italic' }}>
                No probe results (OPENROUTER_API_KEY may not be set).
              </div>
            )}
          </div>

          {geo.queries && geo.queries.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary, #64748b)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Queries used</div>
              {geo.queries.map((q, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)', padding: '6px 0', borderBottom: '1px solid var(--border, #334155)' }}>
                  <span style={{ color: 'var(--text-tertiary, #64748b)', marginRight: 6 }}>{i + 1}.</span>{q}
                </div>
              ))}
            </div>
          )}

          {geo.lastSyncedAt && (
            <div style={{ fontSize: 11, color: 'var(--text-tertiary, #64748b)' }}>
              Last probed: {new Date(geo.lastSyncedAt).toLocaleString()}
            </div>
          )}

          {/* Bot visits section */}
          {geo.botVisits && Object.keys(geo.botVisits).length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary, #64748b)', marginBottom: 8, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
                AI Bot Crawls
              </div>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border, #334155)', borderRadius: 0, overflow: 'hidden' }}>
                {Object.entries(geo.botVisits).sort((a, b) => b[1] - a[1]).map(([bot, count]) => (
                  <div key={bot} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', borderBottom: '1px solid var(--border, #334155)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-secondary, #94a3b8)' }}>{bot}</span>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary, #f1f5f9)' }}>{count.toLocaleString()} visits</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary, #64748b)', marginTop: 6 }}>
                From server access log. Run <code style={{ fontSize: 10 }}>POST /api/{'{brand}'}/observability/ingest-logs</code> to refresh.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
